import {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import DayJS from '../../../helpers/DayJS'
import { toApiDate } from '../../../helpers/formatter/dateFormatters'
import { MeetLauncher } from '../Components/MeetLauncher'
import { ModalMentors } from '../Components/ModalMentors'
import { calculateDynamicFromDate, calculatePredefinedPeriod } from '../Helpers/FilterDatesHelper'
import { useCohorts } from '../Hooks/useCohorts'
import Reports from '../Reports'
import { DateFilterProps } from '../Reports/apis/api-reports'
import { ReportProps } from '../Reports/Components/Report'
import { useTeam } from './TeamContext'
import { SelectedOptions } from '../../../components/Select'

const Context = createContext({} as ReportsContext)
Context.displayName = 'ReportsContext'

export const useReports = () => useContext<ReportsContext>(Context)

export const ReportsContextProvider = ({ children = <Reports /> }: { children?: ReactNode }) => {
    const { courseID, networkId } = useTeam()
    const { cohorts } = useCohorts()
    const reportMenuTab = useState<ReportsActions>('users')

    // Detect if we're in Network context (unified reports)
    // Only true for /network/{id}/reports or /network/{id}/reports/pdf, NOT for /network/{id}/cohorts/{cohortId}/reports
    const isNetworkContext = !!networkId && window.location.pathname.startsWith(`/network/${networkId}/reports`)

    // Read URL query params for filter state (passed from Reports page when navigating to PDF)
    const urlParams = useMemo(() => {
        const params = new URLSearchParams(window.location.search)
        return {
            cohorts: params.get('cohorts')?.split(',').filter(Boolean) || null,
            from: params.get('from') || null,
            to: params.get('to') || null,
        }
    }, [])

    // Track if we initialized from URL params (to prevent effects from overriding)
    const initializedFromUrl = useRef(!!urlParams.cohorts || !!urlParams.from || !!urlParams.to)

    // Reports filters state
    const [optionsSelected, setOptionsSelected] = useState<SelectedOptions | null>(null)
    const [activePeriod, setActivePeriod] = useState<number>(-1) // -1 = Dynamic mode (no button selected)

    // Memoize today to avoid recalculating on every render
    const today = useMemo(() => DayJS(), [])

    // Track previous cohorts to detect real changes
    const prevCohortsRef = useRef<string[] | undefined>()

    // Optimize initial from date calculation - only calculate when cohorts change
    const [initialFromDate, setInitialFromDate] = useState<string>(() => {
        // Use URL param if available, otherwise calculate
        if (urlParams.from) return urlParams.from
        return calculateDynamicFromDate(undefined, cohorts)
    })

    // Update initial from date when cohorts change (deep comparison)
    useEffect(() => {
        // Skip if initialized from URL params
        if (initializedFromUrl.current) return

        if (cohorts && JSON.stringify(cohorts) !== JSON.stringify(prevCohortsRef.current)) {
            const newInitialFromDate = calculateDynamicFromDate(undefined, cohorts)
            if (newInitialFromDate !== initialFromDate) {
                setInitialFromDate(newInitialFromDate)
            }
            prevCohortsRef.current = cohorts
        }
    }, [cohorts, initialFromDate])

    const [reportQuery, setReportQuery] = useState<DateFilterProps>(() => ({
        courseId: courseID || '',
        cohorts: urlParams.cohorts || cohorts || [],
        from: urlParams.from || initialFromDate,
        to: urlParams.to || today.format('YYYY-MM-DD'),
    }))

    const enabled = !!courseID && !!cohorts && !!reportQuery.from && !!reportQuery.to
    const staleTime = 1000 * 60 * 60 // 1 hour

    const setReportQueryFn = (data: Partial<DateFilterProps>) => {
        setReportQuery((prev) => ({
            ...prev,
            ...data,
        }))
    }

    /**
     * Sets a predefined period and updates the dates accordingly
     * Respects the dynamic from date calculation
     */
    const setPredefinedPeriod = (period: number): void => {
        const selectedCohortDates =
            optionsSelected?.length === 0
                ? cohorts || []
                : optionsSelected?.map(({ value }) => toApiDate(`${value}`)) || cohorts || []

        const dynamicFromDate = calculateDynamicFromDate(selectedCohortDates, cohorts, reportQuery.from)
        const periodDates = calculatePredefinedPeriod(period, dynamicFromDate)

        setActivePeriod(period)
        setReportQueryFn({
            from: periodDates.from,
            to: periodDates.to,
        })
    }

    // Smart cohort selection handler
    useEffect(() => {
        if (optionsSelected !== null) {
            const selectedCohortDates: string[] =
                optionsSelected.length === 0 ? cohorts || [] : optionsSelected.map(({ value }) => toApiDate(`${value}`))

            // Check if cohorts actually changed
            if (JSON.stringify(selectedCohortDates) === JSON.stringify(reportQuery.cohorts)) {
                return // No change, skip update
            }

            // Calculate new dynamic from date WITH smart preservation when cohorts change
            const newFromDate = calculateDynamicFromDate(
                selectedCohortDates,
                cohorts,
                reportQuery.from // Pass current from date for smart preservation
            )

            // ALWAYS reset to dynamic mode when cohorts change
            setActivePeriod(-1) // Reset buttons to unselected state

            // Always use dynamic mode behavior when cohorts change
            setReportQueryFn({
                cohorts: selectedCohortDates,
                from: newFromDate,
                to: today.format('YYYY-MM-DD'), // Dynamic mode: from cohort to today
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionsSelected]) // Only depend on optionsSelected, not cohorts

    // Update course ID when it changes
    useEffect(() => {
        if (courseID) {
            setReportQueryFn({ courseId: courseID })
        }
    }, [courseID])

    // Initialize or update cohorts and recalculate dynamic from date (DYNAMIC MODE by default)
    useEffect(() => {
        // IMPORTANT: Don't override if initialized from URL params or user has already made a selection
        if (initializedFromUrl.current) return

        // This effect should only run on initial mount before user makes any selection
        if (
            cohorts &&
            activePeriod === -1 &&
            optionsSelected === null &&
            JSON.stringify(cohorts) !== JSON.stringify(reportQuery.cohorts)
        ) {
            // Only if we're in dynamic mode AND cohorts actually changed AND no user selection yet
            const newFromDate = calculateDynamicFromDate(undefined, cohorts)

            setReportQueryFn({
                cohorts,
                from: newFromDate,
                to: today.format('YYYY-MM-DD'), // Dynamic mode: from cohort to today
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cohorts, activePeriod, optionsSelected])

    // Build a stable react-query key for Reports pages
    const buildReportsQueryKey = ({ section, report }: ReportsQueryKeyParams) => [
        courseID,
        'Reports',
        section,
        report,
        `Cohorts: ${cohorts?.join()}`,
        reportQuery.from,
        reportQuery.to,
    ]

    const calculateChartYAxisMax = useCallback(
        ({
            rows,
            ratio = 1.1,
            skipHeader = true,
        }: {
            rows?: ReportProps['chart']['data']
            ratio?: number
            skipHeader?: boolean
        }) => {
            if (!rows || !Array.isArray(rows) || rows.length <= 1) {
                return { min: 0, max: 10 } // Objeto por defecto
            }

            const data = skipHeader ? rows.slice(1) : rows

            const maxVal = data.reduce((max, row) => {
                const totalRowValue = row.slice(1).reduce((sum, cell) => {
                    if (typeof cell === 'number') {
                        return sum + cell
                    }
                    return sum
                }, 0)

                return totalRowValue > max ? totalRowValue : max
            }, 0)

            const finalMax = maxVal > 0 ? Math.round(maxVal * ratio * 100) / 100 : 10

            return { min: 0, max: finalMax }
        },
        []
    )

    const contextValue: ReportsContext = {
        reportMenuTab,
        optionsSelected,
        setOptionsSelected,
        reportQuery,
        setReportQueryFn,
        activePeriod,
        setActivePeriod,
        setPredefinedPeriod,
        buildReportsQueryKey,
        enabled,
        staleTime,
        calculateChartYAxisMax,
        isNetworkContext,
        networkId: networkId || null,
        courseID: courseID || null,
    }

    return (
        <Context.Provider value={contextValue}>
            {children}
            <MeetLauncher />
            <ModalMentors />
        </Context.Provider>
    )
}

// Updated TypeScript interface
interface ReportsContext {
    reportMenuTab: [ReportsActions, Dispatch<SetStateAction<ReportsActions>>]
    optionsSelected: SelectedOptions | null
    setOptionsSelected: Dispatch<SetStateAction<SelectedOptions | null>>
    reportQuery: DateFilterProps
    setReportQueryFn: (data: Partial<DateFilterProps>) => void
    activePeriod: number
    setActivePeriod: Dispatch<SetStateAction<number>>
    setPredefinedPeriod: (period: number) => void
    enabled: boolean
    staleTime: number
    buildReportsQueryKey: (params: ReportsQueryKeyParams) => string[]
    calculateChartYAxisMax: ({
        rows,
        ratio,
        skipHeader,
    }: {
        rows?: ReportProps['chart']['data']
        ratio?: number
        skipHeader?: boolean
    }) => { min: number; max: number }
    isNetworkContext: boolean
    networkId: string | null
    courseID: string | null
}

export type ReportsActions = 'users' | 'participation' | 'valuation' | 'surveys' | 'cooperation' | 'ai'

type ReportsQueryKeyParams = {
    section: string
    report: string
}